import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../../components/brand/Navbar';
import Footer from '../../components/brand/Footer';
import FAQSection from '../../components/brand/FAQSection';

const faqs = [
  {
    q: 'How many employees can you process in one visit?',
    a: 'Our collectors typically process 15 to 20 employees per hour. For a workplace of 50 employees, a single morning session is usually sufficient. We work with you to schedule individual time slots so employees spend minimal time away from their desks.',
  },
  {
    q: 'What space do we need to provide?',
    a: 'A private room or quiet area where the collector can work with each employee individually. This could be a meeting room, first aid room, or any enclosed space with a chair and a flat surface. We bring all equipment, consumables, and sharps disposal.',
  },
  {
    q: 'What types of workplace health testing do you offer?',
    a: 'We can support corporate health and wellness programs, pre-employment medical screening, annual health checks, cholesterol and diabetes screening, executive health assessments, and any other pathology tests ordered by a doctor. Each employee will need a pathology request form from their GP unless the testing is part of a coordinated workplace health program.',
  },
  {
    q: 'How does billing work for corporate clients?',
    a: 'We invoice the company directly on a per-employee basis after each visit. Volume and frequency-based pricing is available for organisations with regular collection schedules. Contact us for a tailored quote.',
  },
  {
    q: 'Can you visit multiple office locations?',
    a: 'Yes. If your organisation has multiple sites across Brisbane, Sydney and Melbourne metros, we can schedule collections at each location. Contact us to set up a multi-site schedule.',
  },
  {
    q: 'Is there a minimum number of employees required?',
    a: 'There is no strict minimum, but workplace visits are most cost-effective with 5 or more employees per session. For smaller teams, individual at-home collection may be a better option. We are happy to discuss the best approach for your organisation.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const Section = ({ id, title, children }) => (
  <section
    id={id}
    className="py-16"
    style={{ borderBottom: '1px solid rgba(43, 56, 121, 0.08)' }}
  >
    <div className="container max-w-4xl mx-auto px-6">
      <h2
        className="font-['Inter'] font-bold mb-5 text-[#00001E]"
        style={{ fontSize: '1.6rem', letterSpacing: '-0.02em' }}
      >
        {title}
      </h2>
      <div className="text-[#2B3879] leading-relaxed" style={{ fontSize: '1rem' }}>
        {children}
      </div>
    </div>
  </section>
);

const InternalLink = ({ href, children }) => (
  <a href={href} className="text-[#2B3879] hover:underline font-semibold">{children}</a>
);

export default function CorporatePathology() {
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'view_service_page', {
        event_category: 'engagement',
        event_label: 'Corporate Pathology'
      });
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Workplace Blood Testing | Corporate Pathology Brisbane, Sydney &amp; Melbourne | Nura Health</title>
        <meta name="description" content="On-site blood collection for workplaces across Brisbane, Sydney and Melbourne. Reduce staff downtime with convenient mobile pathology at your office. Book a corporate visit." />
        <link rel="canonical" href="https://nurahealth.com.au/services/corporate-pathology" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen" style={{ background: '#F7F9FB' }}>
        <Navbar onBookClick={() => (window.location.href = '/booking')} />

        {/* Hero — dark banner */}
        <section
          className="pt-36 pb-20 relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #00001E 0%, #0a0b2e 60%, #101a3e 100%)' }}
        >
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse at 20% 60%, rgba(128,229,203,0.09) 0%, transparent 55%)',
          }} />
          <div className="container max-w-4xl mx-auto px-6 relative z-10">
            <span className="pill-label-dark">Corporate &amp; Workplace</span>
            <h1
              className="font-['Inter'] font-extrabold mb-5"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.03em', color: '#FFFFFF' }}
            >
              Corporate &amp; Workplace Blood Testing Across Brisbane, Sydney &amp; Melbourne
            </h1>
            <p className="text-lg mb-8 max-w-2xl leading-relaxed" style={{ color: 'rgba(255,255,255,0.80)' }}>
              We bring the collection station to your office — 10 minutes per employee instead of half a day at a pathology centre.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="/booking"
                style={{
                  display: 'inline-block',
                  background: '#80E5CB',
                  color: '#00001E',
                  borderRadius: '8px',
                  padding: '13px 28px',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  textDecoration: 'none',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  boxShadow: '0 4px 16px rgba(128,229,203,0.35)',
                }}
              >
                Get a Quote
              </a>
              <a
                href="mailto:hello@nurahealth.com.au"
                style={{
                  display: 'inline-block',
                  background: 'transparent',
                  color: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '12px 26px',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  textDecoration: 'none',
                  border: '2px solid rgba(255,255,255,0.40)',
                }}
              >
                Email Us
              </a>
            </div>
          </div>
        </section>

        <Section title="Bring Pathology Collection to Your Workplace">
          <p>
            Staff health checks and pathology collection don't have to mean half-day absences. Nura Health sends a qualified
            phlebotomist to your office, warehouse, or work site to collect blood samples on-site. Employees spend 10 minutes
            at the collection station instead of two hours at a pathology centre. It's better for productivity, better for
            engagement, and better for your team's health outcomes.
          </p>
        </Section>

        <Section title="How Workplace Collection Works">
          <p>
            You nominate a date and a private room or quiet space at your workplace. We bring all equipment, consumables,
            and sharps disposal. Employees book individual time slots within your nominated window. Collections are
            completed efficiently — we can typically process 15 to 20 employees per hour. Specimens are transported to
            our partner lab and results go directly to each employee's nominated GP.
          </p>
        </Section>

        <Section title="Common Use Cases">
          <ul className="space-y-3">
            {[
              'Corporate health and wellness programs',
              'Pre-employment medical screening',
              'Annual health checks',
              'Workplace cholesterol and diabetes screening',
              'Executive health assessments',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span className="text-[#2B3879] font-bold mt-0.5">→</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4">We tailor the visit to your organisation's needs.</p>
        </Section>

        <Section title="Get a Quote">
          <p className="mb-6">
            Contact us to discuss your workplace pathology needs. We'll tailor a schedule and pricing to suit
            your team size and collection frequency.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="/booking"
              style={{
                display: 'inline-block',
                background: '#80E5CB',
                color: '#00001E',
                borderRadius: '8px',
                padding: '13px 28px',
                fontWeight: 700,
                fontSize: '0.95rem',
                textDecoration: 'none',
              }}
            >
              Book Online
            </a>
            <a
              href="mailto:hello@nurahealth.com.au"
              style={{
                display: 'inline-block',
                background: 'transparent',
                color: '#2B3879',
                borderRadius: '8px',
                padding: '12px 26px',
                fontWeight: 600,
                fontSize: '0.95rem',
                textDecoration: 'none',
                border: '2px solid #2B3879',
              }}
            >
              Email Us
            </a>
          </div>
        </Section>

        <FAQSection items={faqs} />

        <section className="py-12 bg-[#F7F9FB]">
          <div className="container max-w-4xl mx-auto px-6">
            <p className="text-[#2B3879] text-sm">
              Also see:{' '}
              <InternalLink href="/services/home-blood-collection">home blood collection</InternalLink>
              {' · '}
              <InternalLink href="/">back to home</InternalLink>
            </p>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
